import { defineStore, type Store } from "pinia";
import joi from "joi";
import { useAuthStore } from "@/stores/auth.module";
import md5 from "md5";
import type {
  IDefaultState,
  IDefaultStoreActions,
  IDefultStoreGetters,
  IFileState,
  IImage,
  IInstitution,
} from "@/interfaces";
import type { EditorModel, StoreName, TabErrorSchemaTypes } from "@/types";
import helpers from "@/helpers";
import { globali18n } from "@/i18n";
import type { EmployeeInput } from "@/graphql/generated-types";
import type { InstitutionsEmployeeFragment } from "@/graphql";
import toImageList from "@/helpers/files/toImageList";
import api from "@/api";
import { TRANSLATED_VALIDATION_MESSAGES } from "@/helpers/forms/validate";

const VALIDATION_SCHEMA_UPDATE = joi.object({
  name: joi.string().max(50).required(),
  surname: joi.string().max(50).required(),
  alias: joi.string().max(50).optional().allow(""),
  description: joi.string().max(2000).optional().allow(""),
  email: joi.string().email({ tlds: false }).required(),
  image: joi.array().min(1).max(1),
});

const VALIDATION_SCHEMA_CREATE = joi.object({
  name: joi.string().max(50).required(),
  surname: joi.string().max(50).required(),
  alias: joi.string().max(50).optional().allow(""),
  description: joi.string().max(2000).optional().allow(""),
  email: joi.string().email({ tlds: false }).required(),
  password: joi
    .string()
    .required()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .custom((value: string | undefined | null, helper: any) => {
      const { passwordRetry } = helper.state.ancestors[0];
      const { isValid } = helpers.forms.isValidPassword(
        value as string,
        passwordRetry,
      );

      if (isValid) {
        return true;
      }

      const { t } = globali18n;

      return helper.message(t("global.password_not_acceptable"));
    }),
  image: joi.array().min(1).max(1),
});

type ValidationSchemaTypes = "update" | "create";

const VALIDATION_SCHEMA_MAP: Record<ValidationSchemaTypes, joi.ObjectSchema> = {
  update: VALIDATION_SCHEMA_UPDATE,
  create: VALIDATION_SCHEMA_CREATE,
};

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["name", "surname", "alias", "phone", "mobile"],
  pictures: ["image"],
  security: ["email"],
  login: ["email", "password"],
};

export interface IEmployeeItem {
  id?: string;
  name: string;
  surname: string;
  firstName: string;
  email: string;
  displayName: string;
  lastName: string;
  mobile: string;
  phone: string;
  title: string;
  type: string;
  alias: string;
  password: string;
  passwordRetry: string;
  description: EditorModel;
  showImage: boolean;
  isSuperuser: boolean;
  imageList: IFileState[];
  institution?: IInstitution;
  image?: IImage | null;
  userId?: string;
  employeeTitleId?: string;
}

export interface IEmployeeListFilters {
  titles: string[];
}

export interface IEmpoyeesState extends IDefaultState {
  listFilters: IEmployeeListFilters;
  item: IEmployeeItem;
}

interface EmployeeStoreActions extends IDefaultStoreActions {
  isEmailValid(): Promise<boolean>;
}

export type EmployeesStore = Store<
  string,
  IEmpoyeesState,
  IDefultStoreGetters<IEmpoyeesState>,
  EmployeeStoreActions
>;

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): IEmpoyeesState => ({
      tabErrors: {},
      originalHash: null,
      validation: null,
      item: {
        name: "",
        surname: "",
        firstName: "",
        email: "",
        image: null,
        displayName: "",
        lastName: "",
        mobile: "",
        phone: "",
        title: "",
        type: "",
        alias: "",
        password: "",
        passwordRetry: "",
        description: helpers.quill.getEmptyEditorModel(),
        showImage: false,
        isSuperuser: false,
        imageList: [],
      },
      listFilters: {
        titles: [],
      },
    }),
    actions: {
      hasUnsavedChanges() {
        return !!(
          this.item &&
          this.originalHash &&
          this.originalHash !== this.currentHash()
        );
      },
      currentHash() {
        const item = {
          ...this.getItem(),
          imageList: this.item.imageList,
        };
        return md5(JSON.stringify(item));
      },
      setOriginalHash() {
        // It can take times to TextEditorInput convert html to delta.
        // That's why there is a timeout here.
        const timeout = setTimeout(() => {
          this.originalHash = this.currentHash();
          clearTimeout(timeout);
        }, 10);
      },
      async setItem(data: InstitutionsEmployeeFragment) {
        this.item = {
          id: data.id,
          firstName: data.firstName || "",
          displayName: data.displayName,
          title: data.title || "",
          type: data.type,
          password: "",
          passwordRetry: "",
          showImage: data.showImage || false,
          imageList: toImageList(data),
          description: helpers.quill.htmlToEditorModel(data.description || ""),
          lastName: data.lastName || "",
          alias: data.alias || "",
          phone: data.phone || "",
          mobile: data.mobile || "",
          email: data.user?.email || data.user?.username || "",
          name: data.name || "",
          surname: data.surname || "",
          isSuperuser: data.isSuperuser || false,
          userId: data.user?.id,
          employeeTitleId: data.employeeTitle?.id,
        };

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): EmployeeInput {
        const item = { ...this.item };
        const authStore = useAuthStore();

        return {
          id: item.id?.toString(),
          description: helpers.quill.editorModelToHtml(item.description),
          firstname: item.name,
          lastname: item.surname,
          mobile: item.mobile,
          phone: item.phone,
          alias: item.alias,
          institution: (
            item.institution?.id || authStore.institutionId
          ).toString(),
          image: item.image?.id as string | null | undefined,
          showImage: item.showImage,
          showEmail: true,
          showMobile: true,
          employeeTitle: item.employeeTitleId || null,
        };
      },
      async isNotValid(schema: ValidationSchemaTypes = "update") {
        const validationSchema = VALIDATION_SCHEMA_MAP[schema];
        const data = {
          ...this.item,
          description: helpers.quill.editorModelToText(this.item.description),
          image: this.item.imageList.filter((item) => item.action !== "delete"),
        };
        this.validation = helpers.forms.validate(validationSchema, data);
        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        if (this.validation.isValid) {
          await this.isEmailValid();
        }

        return !this.validation.isValid;
      },
      async isEmailValid() {
        const id = await api.user.getIdByEmailInUse(this.item.email);
        if (id) {
          // Should be different the current userId (if this is an update)
          if (!this.item.userId || this.item.userId !== id) {
            this.validation = {
              ...this.validation,
              isValid: !id,
              errors: {
                email: TRANSLATED_VALIDATION_MESSAGES["email.used"]!,
              },
            };
          }
        }

        return !id;
      },
    },
  });
};

// News View store
export const useEmployeesStore = createUseStore("EMPLOYEES");

// We use the same store structure for the create modal.
export const useAddEmployeeStore = createUseStore("ADD_EMPLOYEES");
